import { RefObject, useEffect, useMemo } from "react";

import type { Product } from "src/api/client-api/products/useGetOneProduct";
import type { BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";
import { URLHandler } from "src/services/URLHandler";
import type { WizardStepKey } from "./useWizardStepsV2";

type Translate = (key: string, fallback: string) => string;

type InitialSectionScrollOptions = {
    isProductDistributionEnabled: boolean;
    refsByKey: Record<WizardStepKey, RefObject<HTMLDivElement | null>>;
    sectionAnchor: string | null;
    startingPageId: string;
};

export const useInitialProductSectionScroll = (options: InitialSectionScrollOptions) => {
    const { isProductDistributionEnabled, refsByKey, sectionAnchor, startingPageId } = options;

    useEffect(() => {
        let targetKey: WizardStepKey | undefined;
        if (sectionAnchor) {
            targetKey = sectionAnchor as WizardStepKey;
        } else {
            const pageIdNum = Number.parseInt(startingPageId);
            if (!Number.isNaN(pageIdNum) && pageIdNum > 0) {
                const v1IndexToKey: WizardStepKey[] = [
                    "identity",
                    "recipe",
                    "packaging",
                    isProductDistributionEnabled ? "distribution" : "consumption",
                    "consumption",
                ];
                targetKey = v1IndexToKey[pageIdNum];
            }
        }

        if (!targetKey || targetKey === "identity") {
            window.scrollTo({ behavior: "auto", left: 0, top: 0 });
            return;
        }
        const ref = refsByKey[targetKey];
        if (!ref?.current) return;
        const id = window.requestAnimationFrame(() => {
            ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return () => window.cancelAnimationFrame(id);
        // Re-run only when URL inputs change. refsByKey is stable by construction.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sectionAnchor, startingPageId]);
};

type ProductBreadcrumbOptions = {
    canonicalProductListUrl: string;
    companyId: number;
    existingProduct: Product | undefined;
    exitDisabled: boolean;
    isIntermediate: boolean;
    isNew: boolean;
    requestExit: (destination: string) => void;
    t: Translate;
};

export const useProductFormBreadcrumbs = (options: ProductBreadcrumbOptions): BreadcrumbType[] => {
    const { canonicalProductListUrl, companyId, existingProduct, exitDisabled, isIntermediate, isNew, requestExit, t } =
        options;

    return useMemo(() => {
        const items: BreadcrumbType[] = [];
        const listLabel =
            isIntermediate ?
                t("sidebar.intermediate_products", "Intermediate products")
            :   t("sidebar.finished_products", "All finished products");
        items.push({
            label: listLabel,
            action: () => requestExit(canonicalProductListUrl),
            disabled: exitDisabled,
        });
        if (existingProduct?.title) {
            items.push({
                label: existingProduct.title,
                disabled: exitDisabled,
                action: () => {
                    if (existingProduct.id !== undefined) {
                        const url =
                            existingProduct.isIntermediate ?
                                URLHandler.intermediateProduct.scores(companyId, existingProduct.id)
                            :   URLHandler.product.scores(companyId, existingProduct.id);
                        requestExit(url);
                    }
                },
            });
        }
        items.push({
            label:
                isNew ?
                    t("product.v2.form.creation_title", "New product")
                :   t("product.form.modification_title", "Modify the product"),
        });
        return items;
    }, [isIntermediate, existingProduct, exitDisabled, isNew, canonicalProductListUrl, companyId, requestExit, t]);
};
